import { useState, useEffect } from 'react';
import { FiRefreshCw, FiAlertCircle, FiCheckCircle, FiClock, FiFileText, FiSearch, FiPhone, FiMessageSquare, FiCalendar } from 'react-icons/fi';
import Select from 'react-select';
import api from '../utils/api';


const StatusBadge = ({ status }) => {
  const styles = {
    SENT: "bg-blue-100 text-blue-800 border-blue-200",
    DELIVERED: "bg-green-100 text-green-800 border-green-200",
    READ: "bg-emerald-100 text-emerald-800 border-emerald-200 ring-1 ring-emerald-500",
    FAILED: "bg-red-100 text-red-800 border-red-200 animate-pulse",
    PENDING: "bg-yellow-100 text-yellow-800 border-yellow-200",
    PROCESSING: "bg-purple-100 text-purple-800 border-purple-200"
  };

  const labels = {
    SENT: "Enviado",
    DELIVERED: "Entregado",
    READ: "Leído",
    FAILED: "Fallido",
    PENDING: "Pendiente",
    PROCESSING: "Procesando"
  };

  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status] || "bg-gray-100 text-gray-800"}`}>
      {labels[status] || status}
    </span>
  );
};

// --- Date Range Utilities ---
const formatDateForApi = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDateRange = (rangeKey) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (rangeKey) {
    case 'TODAY': {
      return { start: today, end: today };
    }
    case 'YESTERDAY': {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return { start: yesterday, end: yesterday };
    }
    case 'THIS_WEEK': {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
      return { start: startOfWeek, end: today };
    }
    case 'LAST_7_DAYS': {
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 6);
      return { start: sevenDaysAgo, end: today };
    }
    case 'THIS_MONTH': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: startOfMonth, end: today };
    }
    case 'LAST_30_DAYS': {
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 29);
      return { start: thirtyDaysAgo, end: today };
    }
    case 'LAST_MONTH': {
      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: startOfLastMonth, end: endOfLastMonth };
    }
    default:
      return { start: today, end: today };
  }
};

const DATE_RANGE_OPTIONS = [
  { key: 'TODAY', label: 'Hoy' },
  { key: 'YESTERDAY', label: 'Ayer' },
  { key: 'THIS_WEEK', label: 'Esta Semana' },
  { key: 'LAST_7_DAYS', label: 'Últimos 7 días' },
  { key: 'THIS_MONTH', label: 'Este Mes' },
  { key: 'LAST_30_DAYS', label: 'Últimos 30 días' },
  { key: 'LAST_MONTH', label: 'Mes Anterior' },
  { key: 'CUSTOM', label: 'Personalizado' },
];

export default function EnviosWhatsappSenpulse() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filterMode, setFilterMode] = useState('ALL'); // 'ALL' or 'FAILED'

  const [syncingIds, setSyncingIds] = useState(new Set());
  const [dateRangeKey, setDateRangeKey] = useState('TODAY');
  const [customStartDate, setCustomStartDate] = useState(formatDateForApi(new Date()));
  const [customEndDate, setCustomEndDate] = useState(formatDateForApi(new Date()));

  const fetchData = async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      let startDate, endDate;
      
      if (dateRangeKey === 'CUSTOM') {
        startDate = customStartDate;
        endDate = customEndDate;
      } else {
        const { start, end } = getDateRange(dateRangeKey);
        startDate = formatDateForApi(start);
        endDate = formatDateForApi(end);
      }
      
      const params = new URLSearchParams({
        fecha_inicio: startDate,
        fecha_fin: endDate,
      });
      const response = await api.get(`/obtener-estado-envios-whatsapp/?${params.toString()}`);
      
      // Simple optimization: compare stringified data to avoid unnecessary re-renders
      // This prevents "flickering" if data hasn't changed
      if (isBackground && data && JSON.stringify(response.data) === JSON.stringify(data)) {
        setLastUpdated(new Date());
        return;
      }

      setData(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  const syncMessage = async (id) => {
    setSyncingIds(prev => new Set(prev).add(id));
    try {
      await api.get(`/sincronizar-envios-whatsapp-sendpulse?id=${id}`);
      // Refresh data silently after sync
      await fetchData(true);
    } catch (error) {
      console.error("Error syncing message", error);
      alert("Error al sincronizar el mensaje");
    } finally {
      setSyncingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [dateRangeKey, customStartDate, customEndDate]);

  const filteredLogs = data?.logs.filter(log => {
    if (filterMode === 'FAILED') return log.status === 'FAILED';
    return true;
  }) || [];

  return (
    <div className="min-h-screen font-sans animate-in fade-in duration-500">
      
      {/* KPI CARDS - Siguiendo el estilo de tarjetas del dashboard pero más compactas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="bg-white shadow-md rounded-xl p-4 border border-gray-100">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Total ({DATE_RANGE_OPTIONS.find(o => o.key === dateRangeKey)?.label})</p>
              <h3 className="text-2xl font-bold text-gray-800">{loading ? '...' : data?.summary.total}</h3>
            </div>
            <div className="p-2 bg-blue-50 rounded-lg">
              <FiMessageSquare className="text-blue-600 text-lg" />
            </div>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-xl p-4 border border-gray-100">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Tasa Entrega</p>
              <h3 className="text-2xl font-bold text-gray-800">{loading ? '...' : `${data?.summary.success_rate}%`}</h3>
            </div>
            <div className="p-2 bg-green-50 rounded-lg">
              <FiCheckCircle className="text-green-600 text-lg" />
            </div>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-xl p-4 border border-gray-100">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">En Cola</p>
              <h3 className="text-2xl font-bold text-gray-800">{loading ? '...' : data?.summary.pending}</h3>
            </div>
            <div className="p-2 bg-amber-50 rounded-lg">
              <FiClock className="text-amber-600 text-lg" />
            </div>
          </div>
        </div>

        <div className={`shadow-md rounded-xl p-4 border transition-all ${data?.summary.failed > 0 ? 'bg-red-50 border-red-100' : 'bg-white border-gray-100'}`}>
          <div className="flex justify-between items-center">
            <div>
              <p className={`text-[10px] uppercase tracking-wider font-bold ${data?.summary.failed > 0 ? 'text-red-500' : 'text-gray-400'}`}>Fallidos</p>
              <h3 className={`text-2xl font-bold ${data?.summary.failed > 0 ? 'text-red-700' : 'text-gray-800'}`}>{loading ? '...' : data?.summary.failed}</h3>
            </div>
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <FiAlertCircle className="text-red-600 text-lg" />
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTAINER (Estilo ListadoCtlPendientes) */}
      <div className="bg-white shadow-xl rounded-xl p-4 md:p-6 w-full border border-gray-200">
        
        {/* TOOLBAR */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">
            
            {/* Selector de Filtro (Mismo estilo que CtlPendientes) */}
            <Select
              value={[
                { value: 'ALL', label: 'Todos los estados' },
                { value: 'FAILED', label: 'Solo Fallidos' }
              ].find(opt => opt.value === filterMode)}
              onChange={(selected) => setFilterMode(selected.value)}
              options={[
                { value: 'ALL', label: 'Todos los estados' },
                { value: 'FAILED', label: 'Solo Fallidos' }
              ]}
              styles={{
                control: (base, state) => ({
                  ...base,
                  minWidth: '180px',
                  borderRadius: '0.5rem',
                  borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: '#2563eb'
                  },
                  fontSize: '0.875rem'
                }),
                option: (base, state) => ({
                  ...base,
                  fontSize: '0.875rem',
                  backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#eff6ff' : 'white',
                  color: state.isSelected ? 'white' : '#4b5563'
                })
              }}
            />

            {/* Date Range Selector */}
            <div className="w-full md:w-48">
              <Select
                value={DATE_RANGE_OPTIONS.map(opt => ({ value: opt.key, label: opt.label })).find(opt => opt.value === dateRangeKey)}
                onChange={(selected) => setDateRangeKey(selected.value)}
                options={DATE_RANGE_OPTIONS.map(opt => ({ value: opt.key, label: opt.label }))}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderRadius: '0.5rem',
                    borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: '#2563eb'
                    },
                    fontSize: '0.875rem'
                  }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: '0.875rem',
                    backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#eff6ff' : 'white',
                    color: state.isSelected ? 'white' : '#4b5563'
                  })
                }}
              />
            </div>

            {/* Custom Date Inputs */}
            {dateRangeKey === 'CUSTOM' && (
              <div className="flex items-center gap-2 w-full md:w-auto">
                <input 
                  type="date" 
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
                <span className="text-gray-400 text-sm">a</span>
                <input 
                  type="date" 
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>
            )}

            <button 
              onClick={fetchData} 
              className="p-2 bg-gray-50 rounded-full hover:bg-gray-200 text-gray-600 transition-colors"
              title="Recargar"
            >
              <FiRefreshCw className={loading ? "animate-spin text-blue-500" : ""} />
            </button>
          </div>

          {lastUpdated && (
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest hidden md:block">
              Sincronizado: {lastUpdated.toLocaleTimeString()}
            </div>
          )}
        </div>

        {/* DATA TABLE (Mismo estilo que CtlPendientes) */}
        <div className="overflow-x-auto rounded-lg border border-gray-100 min-h-[400px]">
          <table className="min-w-full table-auto text-sm text-gray-700">
            <thead className="bg-blue-50 text-gray-600 uppercase text-xs font-bold tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Hora / Fecha</th>
                <th className="px-4 py-3 text-left">Cliente / Contacto</th>
                <th className="px-4 py-3 text-left">Plantilla / Recurso</th>
                <th className="px-4 py-3 text-center">Estado</th>
                <th className="px-4 py-3 text-left">Detalles Técnicos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="5" className="py-20">
                    <div className="flex flex-col items-center justify-center gap-2 text-gray-500">
                      <FiRefreshCw className="animate-spin text-blue-500" size={32} />
                      <span>Obteniendo datos de SendPulse...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-20">
                    <div className="flex flex-col items-center justify-center gap-2 text-gray-500">
                      <FiAlertCircle size={32} className="text-gray-300" />
                      <span>No se encontraron registros</span>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors group">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-bold text-gray-900">{log.created_at.split(' ')[1]}</div>
                      <div className="text-[10px] text-gray-400 font-medium">{log.created_at.split(' ')[0]}</div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{log.customer_name || "N/A"}</div>
                      <div className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <FiPhone size={10} /> {log.phone}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded inline-block mb-1 border border-blue-100">
                        {log.template_name}
                      </div>
                      {log.pdf_name && (
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                          <FiFileText className="text-gray-400" />
                          <span className="truncate max-w-[150px]">{log.pdf_name}</span>
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={log.status} />
                    </td>

                    <td className="px-4 py-3">
                      {log.status === 'FAILED' ? (
                        <div className="bg-red-50 border border-red-100 p-2 rounded text-[11px] text-red-700 font-medium">
                          <FiAlertCircle className="inline mr-1" />
                          {log.error_message || "Error desconocido"}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex flex-col">
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">API ID</span>
                            <span className="text-[11px] text-gray-600 font-mono truncate max-w-[150px]" title={log.sp_message_id}>
                              {log.sp_message_id || '-'}
                            </span>
                          </div>
                          
                          <button 
                            onClick={() => syncMessage(log.id)}
                            disabled={syncingIds.has(log.id)}
                            className={`p-1.5 rounded-full hover:bg-blue-50 text-blue-500 transition-all ${syncingIds.has(log.id) ? 'bg-blue-50' : ''}`}
                            title="Sincronizar este mensaje"
                          >
                            <FiRefreshCw size={14} className={syncingIds.has(log.id) ? "animate-spin" : ""} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* FOOTER (Estilo CtlPendientes) */}
        {!loading && (
          <div className="flex flex-col md:flex-row justify-between items-center mt-6 text-sm text-gray-600 gap-4 border-t border-gray-100 pt-4">
            <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
              Sincronizado cada 30 segundos
            </div>
          </div>
        )}
      </div>
    </div>
  );
}